import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '@/auth/useAuth';
import { PRIVACY_URL, TERMS_URL } from '@/config/legal';
import { Button, ListRow, Screen, SectionLabel } from '../ui';

/** Ported from src/app/(main)/account.tsx. */
export default function Account() {
  const navigate = useNavigate();
  const { user, signOut, deleteAccount } = useAuth();
  const dialog = useRef<HTMLDialogElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function reallyDelete(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await deleteAccount();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Your account could not be deleted.');
    } finally {
      setBusy(false);
      dialog.current?.close();
    }
  }

  return (
    <Screen title="Account">
      {user?.email && <p className="type-body project-meta">{user.email}</p>}

      <SectionLabel>Billing</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="password" label="Credits" onPress={() => navigate('/credits')} />
      </div>

      <SectionLabel>Legal</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="account" label="Terms of Use" onPress={() => window.open(TERMS_URL, '_blank', 'noreferrer')} divider />
        <ListRow icon="account" label="Privacy Policy" onPress={() => window.open(PRIVACY_URL, '_blank', 'noreferrer')} />
      </div>

      <SectionLabel>This account</SectionLabel>
      <div className="card clay-card detail-rows">
        <ListRow icon="signOut" label="Sign out" accent onPress={() => void signOut().then(() => navigate('/'))} divider />
        <ListRow icon="delete" label="Delete my account" destructive onPress={() => dialog.current?.showModal()} />
      </div>

      {error && <p role="alert" className="type-caption field-error-text">{error}</p>}

      {/* A <dialog>, not window.confirm: a native modal blocks the page and cannot be tested. */}
      <dialog ref={dialog} aria-label="Delete your account" className="confirm-dialog">
        <h2 className="type-title">Delete your account?</h2>
        <p className="type-body">
          This removes your account and the apps you have built. It cannot be undone.
        </p>
        <div className="build-actions">
          <Button title="Yes, delete it" variant="dangerSoft" loading={busy} onPress={() => void reallyDelete()} />
          <Button title="Cancel" variant="ghost" onPress={() => dialog.current?.close()} />
        </div>
      </dialog>
    </Screen>
  );
}
